import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '../constants/routes';
import { useMutation } from '@tanstack/react-query';
import { acceptInviteApi, checkEmailApi } from '../utils/apis';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControl,
  FormLabel,
} from '@mui/material';
import { CheckCircle, XCircle, Eye, EyeSlash } from '@phosphor-icons/react';
import quibbleaiLogo from '../assets/images/quibbleaiLogoDark.svg';
import theme from '../theme';
import { commonStyles } from '../utils/styles/inputs';

export default function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const emailParam = searchParams.get('email') || '';
  const orgCodeParam = searchParams.get('organizationCode') || '';

  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [userExists, setUserExists] = useState<boolean | null>(null);
  const [isCheckingUser, setIsCheckingUser] = useState(false);

  useEffect(() => {
    if (!emailParam || !emailParam.includes('@') || !emailParam.includes('.')) {
      setUserExists(false);
      return;
    }
    let cancelled = false;
    setIsCheckingUser(true);
    checkEmailApi(emailParam)
      .then(res => {
        if (!cancelled) setUserExists(res.data?.exists ?? false);
      })
      .catch(() => {
        if (!cancelled) setUserExists(false);
      })
      .finally(() => {
        if (!cancelled) setIsCheckingUser(false);
      });
    return () => {
      cancelled = true;
    };
  }, [emailParam]);

  const {
    mutateAsync: acceptInvite,
    isPending,
    error,
    isError,
  } = useMutation({
    mutationFn: acceptInviteApi,
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await acceptInvite({
      email: emailParam,
      otp,
      organizationCode: orgCodeParam,
      name: userExists ? '' : name,
      password: userExists ? '' : password,
    });
  };

  const errorMessage =
    (error as any)?.response?.data?.message ||
    'Something went wrong. Please try again.';

  const isFormValid =
    emailParam &&
    orgCodeParam &&
    otp &&
    (userExists === true ? true : !!(name && password));

  if (isSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.secondary.main,
          position: 'relative',
        }}
      >
        <Box sx={{ p: 3, position: 'absolute', top: 0, left: 0 }}>
          <img src={quibbleaiLogo} alt="Quibble AI" style={{ width: '8rem' }} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexGrow: 1,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '507px',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '12px',
              gap: '24px',
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.lightGray.main}`,
              boxShadow: '0px 18px 45px rgba(15, 23, 42, 0.04)',
            }}
          >
            <CheckCircle
              size={64}
              weight="fill"
              color="#22C55E"
              style={{
                background: 'rgba(34, 197, 94, 0.12)',
                borderRadius: '50%',
                padding: '8px',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '22px',
                color: theme.palette.custom.textBlue,
                textAlign: 'center',
              }}
            >
              Account Created Successfully
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '14px',
                textAlign: 'center',
                maxWidth: '360px',
              }}
            >
              Your account has been created and you've joined the organization.
              Please login to access the platform.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate(routes.login)}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '8px',
                py: 1.25,
                boxShadow: 'none',
              }}
            >
              Login to Platform
            </Button>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.secondary.main,
        position: 'relative',
      }}
    >
      <Box sx={{ p: 3, position: 'absolute', top: 0, left: 0 }}>
        <img src={quibbleaiLogo} alt="Quibble AI" style={{ width: '8rem' }} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          px: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '507px',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '12px',
            gap: '20px',
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.lightGray.main}`,
            boxShadow: '0px 18px 45px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '22px',
                color: theme.palette.custom.textBlue,
              }}
            >
              Accept Organization Invite
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '14px', mt: 1 }}
            >
              {userExists === true
                ? 'Enter the verification code to join the organization'
                : 'Complete your account to join the organization'}
            </Typography>
          </Box>

          {isError && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: '8px',
                bgcolor: 'rgba(248, 113, 113, 0.08)',
                border: `1px solid ${theme.palette.error.main}33`,
              }}
            >
              <XCircle
                size={20}
                color={theme.palette.error.main}
                weight="fill"
              />
              <Typography
                sx={{ fontSize: '13px', color: theme.palette.error.main }}
              >
                {errorMessage}
              </Typography>
            </Box>
          )}

          {isCheckingUser ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                gap: 2,
              }}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">
                Checking account...
              </Typography>
            </Box>
          ) : (
            <form
              onSubmit={e => void handleSubmit(e)}
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <FormControl fullWidth size="small">
                <FormLabel sx={commonStyles.formLabel}>Email</FormLabel>
                <TextField
                  size="small"
                  value={emailParam}
                  fullWidth
                  disabled
                  sx={{
                    ...commonStyles.textField,
                    '& .MuiInputBase-root.Mui-disabled': {
                      bgcolor: '#F8F9FC',
                      color: theme.palette.text.secondary,
                      WebkitTextFillColor: theme.palette.text.secondary,
                    },
                  }}
                />
              </FormControl>

              <FormControl fullWidth size="small">
                <FormLabel sx={commonStyles.formLabel}>
                  Organization Code
                </FormLabel>
                <TextField
                  size="small"
                  value={orgCodeParam}
                  fullWidth
                  disabled
                  sx={{
                    ...commonStyles.textField,
                    '& .MuiInputBase-root.Mui-disabled': {
                      bgcolor: '#F8F9FC',
                      color: theme.palette.text.secondary,
                      WebkitTextFillColor: theme.palette.text.secondary,
                    },
                  }}
                />
              </FormControl>

              <FormControl fullWidth size="small">
                <FormLabel sx={commonStyles.formLabel}>
                  Verification Code (OTP)
                </FormLabel>
                <TextField
                  placeholder="Enter the OTP from your email"
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  autoFocus
                  sx={commonStyles.textField}
                />
              </FormControl>

              {userExists === false && (
                <>
                  <FormControl fullWidth size="small">
                    <FormLabel sx={commonStyles.formLabel}>Full Name</FormLabel>
                    <TextField
                      placeholder="Enter your full name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      fullWidth
                      required
                      size="small"
                      sx={commonStyles.textField}
                    />
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <FormLabel sx={commonStyles.formLabel}>Password</FormLabel>
                    <TextField
                      placeholder="Create a password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      fullWidth
                      required
                      size="small"
                      sx={commonStyles.textField}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? (
                                <EyeSlash size={18} />
                              ) : (
                                <Eye size={18} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!isFormValid || isPending}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: '8px',
                  mt: 1,
                  py: 1.25,
                  boxShadow: 'none',
                }}
              >
                {isPending ? (
                  <CircularProgress size={22} color="inherit" />
                ) : userExists === true ? (
                  'Join Organization'
                ) : (
                  'Create Account & Join'
                )}
              </Button>
            </form>
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', fontSize: '13px' }}
          >
            Already have an account?{' '}
            <Typography
              component="span"
              onClick={() => navigate(routes.login)}
              sx={{
                fontSize: '13px',
                color: theme.palette.primary.main,
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Login here
            </Typography>
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}
